// 問い合わせフォームのバリデーション
// vuelidateのラッパーなので、利用の際はvuelidateのドキュメント(https://vuelidate-next.netlify.app/)も参照ください。

import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, email, helpers } from '@vuelidate/validators';
import { telDigitLength, telFormat } from '@/javascripts/utils/custom_validators';

import type { Ref } from 'vue';
import type { Validation, ValidationRuleCollection } from '@vuelidate/core';
import type Inquiry from '@/javascripts/utils/inquiry';

/** バリデーションする項目 */
export type ValidationKey =
  'employmentTypeCd' | 'wishfulTimeCd' | 'prefectureCd' |
  'lastName' | 'firstName' | 'lastNameKana' | 'firstNameKana' |
  'birthYearCd' | 'phone' | 'email' | 'comment' | 'workFrequencyCd';

/** お名前のバリデーションルール */
const nameValidators: ValidationRuleCollection<string> = {
  required: helpers.withMessage('お名前を入力してください', required),
  maxLength: helpers.withMessage('30文字以内で入力してください', maxLength(30))
};

/** ふりがなのバリデーションルール */
const kanaValidators: ValidationRuleCollection<string> = {
  required: helpers.withMessage('ふりがなを入力してください', required),
  maxLength: helpers.withMessage('30文字以内で入力してください', maxLength(30))
};

/** バリデーションルールのテンプレート */
export type ValidationRules = {
  [keys in ValidationKey]: ValidationRuleCollection<Inquiry[keys]>
};

export const rules: ValidationRules = {
  employmentTypeCd: {
    required: helpers.withMessage('ご希望の雇用形態を選択してください', required)
  },
  wishfulTimeCd: {
    required: helpers.withMessage('ご希望の転職時期を選択してください', required)
  },
  prefectureCd: {
    required: helpers.withMessage('お住まいの都道府県を選択してください', required)
  },
  lastName: nameValidators,
  firstName: nameValidators,
  lastNameKana: kanaValidators,
  firstNameKana: kanaValidators,
  birthYearCd: {
    required: helpers.withMessage('生まれ年を選択してください', required)
  },
  phone: {
    required: helpers.withMessage('電話番号を入力してください', required),
    telDigitLength: helpers.withMessage('電話番号の桁数が正しくありません', telDigitLength),
    telFormat: helpers.withMessage('電話番号が正しくありません', telFormat)
  },
  email: {
    email: helpers.withMessage('メールアドレスが正しくありません', email),
    maxLength: helpers.withMessage('80文字以内で入力してください', maxLength(80))
  },
  comment: {
    maxLength: helpers.withMessage('400文字以内で入力してください', maxLength(400))
  },
  workFrequencyCd: {
    required: helpers.withMessage('ご希望の勤務日数を選択してください', required)
  },
}

/** バリデーション対象となる問い合わせオブジェクト */
type InquiryObj = Pick<Inquiry, ValidationKey>;

/** 問い合わせバリデーション */
export type InquiryValidation = Validation<ValidationRules, InquiryObj>;

/** バリデーションルール(バリデーション項目を指定) */
export type PickValidationRules<T extends ValidationKey> = Pick<ValidationRules, T>;
/** 問い合わせバリデーション(バリデーション項目を指定) */
export type PickInquiryValidation<T extends ValidationKey> = Validation<PickValidationRules<T>, InquiryObj>;

/**
 * 問い合わせフォームのバリデーションに関するコンポーザブル関数
 * @param inquiry バリデーションする問い合わせインスタンス
 */
export function useInquiryValidation(inquiry: InquiryObj | Ref<InquiryObj>): Ref<InquiryValidation>;

/**
 * 問い合わせフォームのバリデーションに関するコンポーザブル関数(バリデーション項目を指定)
 * @param inquiry バリデーションする問い合わせインスタンス
 * @param validationFields バリデーションする項目
 */
export function useInquiryValidation<T extends ValidationKey>(
  inquiry: InquiryObj | Ref<InquiryObj>,
  validationFields: T[]
): Ref<PickInquiryValidation<T>>;

// オーバーロード関数の実装
export function useInquiryValidation<T extends ValidationKey>(
  inquiry: InquiryObj | Ref<InquiryObj>,
  validationFields?: T[]
) {
  if (validationFields) {
    const pickRules = validationFields.reduce((obj, key) => {
      obj[key] = rules[key];
      return obj;
    }, {} as PickValidationRules<T>);
    return useVuelidate(pickRules, inquiry, { $autoDirty: true });
  }
  else {
    return useVuelidate(rules, inquiry, { $autoDirty: true });
  }
}

export default useInquiryValidation;
